/* eslint-disable react-hooks/set-state-in-effect -- Firestore listener setup resets state when breweryId changes */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

import { db } from '../utils/firebase';
import { buildReviewSeenKey } from '../utils/reviewDetails/buildReviewSeenDocId';

const EMPTY_SEEN_KEYS = new Set();

/**
 * @param {string | null | undefined} breweryId
 * @returns {{ seenKeys: Set<string>, loading: boolean, error: string | null }}
 */
function useBreweryReviewSeenSet(breweryId) {
  const [seenKeys, setSeenKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isActive = typeof breweryId === 'string' && breweryId.length > 0;

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    setLoading(true);
    setError(null);

    const seenRef = collection(db, 'breweryReviewSeen');
    const seenQuery = query(seenRef, where('breweryId', '==', breweryId));

    const unsubscribe = onSnapshot(
      seenQuery,
      (snapshot) => {
        const nextKeys = new Set();
        snapshot.docs.forEach((seenDoc) => {
          const data = seenDoc.data();
          const reviewType = typeof data.reviewType === 'string' ? data.reviewType : '';
          const reviewId = typeof data.reviewId === 'string' ? data.reviewId : '';
          if (reviewType.length > 0 && reviewId.length > 0) {
            nextKeys.add(buildReviewSeenKey(reviewType, reviewId));
          }
        });
        setSeenKeys(nextKeys);
        setLoading(false);
      },
      (snapshotError) => {
        setError(snapshotError.message);
        setSeenKeys(new Set());
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [breweryId, isActive]);

  const stableSeenKeys = useMemo(() => seenKeys, [seenKeys]);

  return {
    seenKeys: isActive ? stableSeenKeys : EMPTY_SEEN_KEYS,
    loading: isActive ? loading : false,
    error: isActive ? error : null,
  };
}

export default useBreweryReviewSeenSet;
